import { useState, useEffect, useRef, useCallback } from 'react';
import { useIsAuthenticated } from '@azure/msal-react';
import { Bell, X } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { haalMeldingen, markeerMeldingenGelezen, verwijderMelding, wisAlleMeldingen } from '../lib/api.js';

// Relatieve tijd in het Nederlands ("2 min geleden", "3 uur geleden", "gisteren", datum).
function relatieveTijd(iso) {
  const t = Date.parse(iso || '');
  if (Number.isNaN(t)) return '';
  const sec = Math.floor((Date.now() - t) / 1000);
  if (sec < 60) return 'zojuist';
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} min geleden`;
  const uur = Math.floor(min / 60);
  if (uur < 24) return `${uur} uur geleden`;
  const dag = Math.floor(uur / 24);
  if (dag === 1) return 'gisteren';
  if (dag < 7) return `${dag} dagen geleden`;
  return new Date(t).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

export default function MeldingBel() {
  const isAuthenticated = useIsAuthenticated();
  const [meldingen, setMeldingen] = useState([]);
  const [ongelezen, setOngelezen] = useState(0);
  const [open, setOpen] = useState(false);
  const paneelRef = useRef(null);
  const { pathname } = useLocation();

  const laad = useCallback(async () => {
    try {
      const res = await haalMeldingen();
      setMeldingen(res.meldingen || []);
      setOngelezen(res.ongelezen || 0);
    } catch {
      // stil: geen meldingen tonen bij fout/onbereikbaar
    }
  }, []);

  // Bij inloggen en bij elke navigatie verversen (setState in .then, net als RuilBadgeWatcher).
  useEffect(() => {
    if (!isAuthenticated) return;
    let afgebroken = false;
    haalMeldingen()
      .then((res) => {
        if (afgebroken) return;
        setMeldingen(res.meldingen || []);
        setOngelezen(res.ongelezen || 0);
      })
      .catch(() => { /* stil: geen meldingen tonen bij fout/onbereikbaar */ });
    return () => { afgebroken = true; };
  }, [isAuthenticated, pathname]);

  // Klik buiten het paneel sluit het.
  useEffect(() => {
    if (!open) return;
    const opClick = (e) => { if (paneelRef.current && !paneelRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', opClick);
    return () => document.removeEventListener('mousedown', opClick);
  }, [open]);

  if (!isAuthenticated) return null;

  const openen = async () => {
    const nieuweStand = !open;
    setOpen(nieuweStand);
    // Bij openen: alles als gelezen markeren (badge verdwijnt), lijst blijft staan.
    if (nieuweStand && ongelezen > 0) {
      setOngelezen(0);
      try { await markeerMeldingenGelezen(); } catch { /* stil */ }
    }
  };

  const verwijderEen = async (id) => {
    setMeldingen((lijst) => lijst.filter((m) => m.id !== id));
    try { await verwijderMelding(id); } catch { laad(); }
  };

  const wisAlles = async () => {
    setMeldingen([]);
    try { await wisAlleMeldingen(); } catch { laad(); }
  };

  return (
    <div className="lg:relative" ref={paneelRef}>
      <button
        onClick={openen}
        className="relative p-2 rounded-md text-brand-100 hover:text-white hover:bg-white/10 transition-colors"
        title="Meldingen"
      >
        <Bell className="w-5 h-5" />
        {ongelezen > 0 && (
          <span className="absolute -top-0.5 -right-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-xs font-semibold leading-none">
            {ongelezen > 9 ? '9+' : ongelezen}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute left-4 right-4 mt-2 max-h-96 overflow-y-auto rounded-lg bg-white shadow-xl border border-slate-200 z-50 text-slate-800 lg:left-auto lg:right-0 lg:w-80">
          <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-100">
            <span className="text-sm font-semibold text-slate-700">Meldingen</span>
            {meldingen.length > 0 && (
              <button onClick={wisAlles} className="text-xs text-slate-400 hover:text-red-600">Alles wissen</button>
            )}
          </div>
          {meldingen.length === 0 ? (
            <div className="px-4 py-8 text-center text-sm text-slate-400">Geen meldingen</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {meldingen.map((m) => (
                <li key={m.id} className={`px-4 py-3 flex items-start gap-2 ${m.gelezen ? 'bg-white' : 'bg-brand-50/60'}`}>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm ${m.gelezen ? 'text-slate-600' : 'text-slate-800 font-medium'}`}>{m.tekst}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{relatieveTijd(m.aangemaaktOp)}</p>
                  </div>
                  <button
                    onClick={() => verwijderEen(m.id)}
                    className="shrink-0 text-slate-300 hover:text-red-600"
                    title="Verwijder deze melding"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
